import axios from 'axios';
declare module 'axios' {
  export interface AxiosStatic {
    logout: () => void;
    getBaseUrl: () => string;
    setAutoLogoutHandler: (handler: () => void) => void;
  }
}

// Add a function reference to store the auto logout handler
let autoLogoutHandler: (() => void) | null = null;

// Add method to set the auto logout handler from AuthProvider
axios.setAutoLogoutHandler = (handler: () => void) => {
  autoLogoutHandler = handler;
};

axios.defaults.baseURL = `${window.location.origin}/api/`;
axios.defaults.withCredentials = true;

axios.getBaseUrl = () => {
  return axios.defaults.baseURL || '';
};

axios.logout = async () => {
  await axios.post('logout/');
};

const redirectToNotFound = () => {
  window.location.hash = '/not-found';
};

const maintenanceMsg = 'Bento is currently down for maintenance, please try again later.';

const HTTP_STATUS_MESSAGE_MAP = {
  403: 'You do not have permission to perform this action.',
  404: 'Not found.',
  413: 'Sorry, that file is too big. Please contact Bento at smile@bento.net.',
  503: maintenanceMsg,
  504: maintenanceMsg,
};

const UNKNOWN_STATUS_ERROR_MESSAGE =
  "Sorry! There's a problem on our end. If the issue persists, contact Bento at smile@bento.net.";

// helper to format error messages
const formatErrorMessage = (errors: any): string => {
  if (errors == null) {
    return UNKNOWN_STATUS_ERROR_MESSAGE;
  }
  if (typeof errors === 'object') {
    return Object.values(errors).join(', ');
  }
  return errors;
};

axios.interceptors.request.use(
  (config) => config,
  (error) => {
    let message = UNKNOWN_STATUS_ERROR_MESSAGE;
    if (error.request) {
      const status = error.request.status as keyof typeof HTTP_STATUS_MESSAGE_MAP;
      if (status in HTTP_STATUS_MESSAGE_MAP) {
        message = HTTP_STATUS_MESSAGE_MAP[status as keyof typeof HTTP_STATUS_MESSAGE_MAP];
      }
    }

    // eslint-disable-next-line no-param-reassign
    error.message = message;

    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    let message = UNKNOWN_STATUS_ERROR_MESSAGE;

    if (!error.response) {
      message = 'Bento is unavailable, try again later';
    } else {
      const { status, data } = error.response;

      if (status === 404) {
        redirectToNotFound();
      }

      if (status === 401) {
        // A 401 from logout means the session is already gone. Treat it as a
        // completed logout instead of feeding it back into the auto-logout
        // handler and recursively posting to this endpoint.
        if (error.config?.url === 'logout/') {
          return;
        }
        if (autoLogoutHandler) {
          autoLogoutHandler();
        } else {
          axios.logout();
        }
        return;
      }

      // try to pull error details from the response payload.
      const errorDetails = data?.errors || data?.data?.errors;
      if (errorDetails) {
        message = formatErrorMessage(errorDetails);
      } else if (status in HTTP_STATUS_MESSAGE_MAP) {
        message = HTTP_STATUS_MESSAGE_MAP[status as keyof typeof HTTP_STATUS_MESSAGE_MAP];
      }
    }

    error.message = message;
    error.name = ''; // hide the error name from the user.

    return Promise.reject(error);
  }
);

export default axios;
